import { useRouter } from 'next/router'
import React, { VFC, useState } from 'react'
import {
  createContentBookmark,
  deleteContentBookmark,
} from 'src/api/contentBookmark'
import { createContentLike, deleteContentLike } from 'src/api/contentLike'
import { useLoginUser } from 'src/api/user'
import { BookmarkFill, BookmarkLine, ContentLike } from 'src/assets/svgs'
import { UserIcon } from 'src/components/Image/UserIcon'
import { useToastContext } from 'src/contexts/ToastContext'
import { Link } from 'src/elements/Link'
import { Content } from 'src/models/Content'
import { SIGN_IN, contentDetail } from 'src/utils/routes/clientPaths'
import styled, { css } from 'styled-components'

export const ContentCard: VFC<{ content: Content }> = ({ content }) => {
  const router = useRouter()
  const { user } = useLoginUser()
  const { showSuccess } = useToastContext()
  const [isLiked, setIsLiked] = useState(content.isLiked)
  const [isBookmarked, setIsBookmarked] = useState(content.isBookmarked)
  const [likesCount, setLikesCount] = useState(content.likesCount)

  // contentのpropsが変更された場合に状態を更新
  React.useEffect(() => {
    setIsLiked(content.isLiked)
    setIsBookmarked(content.isBookmarked)
    setLikesCount(content.likesCount)
  }, [content.isLiked, content.isBookmarked, content.likesCount])

  const handleLikeClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // ログインしていない場合はログイン画面にリダイレクト
    if (!user) {
      router.push(SIGN_IN)
      return
    }

    try {
      if (isLiked) {
        const response = await deleteContentLike(content.id)
        // APIレスポンスが成功した場合のみ状態を更新
        if (response?.data?.success) {
          setIsLiked(false)
          setLikesCount((prev) => Math.max(0, prev - 1))
        }
      } else {
        const response = await createContentLike(content.id)
        // APIレスポンスが成功した場合のみ状態を更新
        if (response?.data?.success) {
          setIsLiked(true)
          setLikesCount((prev) => prev + 1)
        }
      }
    } catch (error) {
      console.error('Like toggle failed:', error)
    }
  }

  const handleBookmarkClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // ログインしていない場合はログイン画面にリダイレクト
    if (!user) {
      router.push(SIGN_IN)
      return
    }

    try {
      if (isBookmarked) {
        const response = await deleteContentBookmark(content.id)
        // APIレスポンスが成功した場合のみ状態を更新
        if (response?.data?.success) {
          setIsBookmarked(false)
          showSuccess('ブックマークを削除しました')
        }
      } else {
        const response = await createContentBookmark(content.id)
        // APIレスポンスが成功した場合のみ状態を更新
        if (response?.data?.success) {
          setIsBookmarked(true)
          showSuccess('ブックマークしました！')
        }
      }
    } catch (error) {
      console.error('Bookmark toggle failed:', error)
    }
  }

  return (
    <CardWrapper>
      <Link href={contentDetail(content.id)}>
        <ContentHeader>
          <ContentTitle>{content.title}</ContentTitle>
          <BookmarkButton onClick={handleBookmarkClick}>
            {isBookmarked ? <BookmarkFill /> : <BookmarkLine />}
          </BookmarkButton>
        </ContentHeader>

        <ContentAuthor>
          <AuthorInfo>
            <AuthorAvatar>
              <UserIcon src={content.companyImageUrl} size="32px" />
            </AuthorAvatar>
            <AuthorDetails>
              <CompanyName>{content.companyName}</CompanyName>
              <AuthorName>{content.companyAdminName}</AuthorName>
            </AuthorDetails>
          </AuthorInfo>
        </ContentAuthor>

        <ContentBody>
          <ContentThumbnail>
            <ContentImage src={content.imageUrl} alt={content.title} />
          </ContentThumbnail>
          <ContentExcerpt>
            {(() => {
              const tempDiv = document.createElement('div')
              tempDiv.innerHTML = content.body.replace(
                /<style[^>]*>[\s\S]*?<\/style>|<[^>]*>/g,
                '',
              )
              return tempDiv.textContent || tempDiv.innerText || ''
            })().substring(0, 200)}
            {(() => {
              const tempDiv = document.createElement('div')
              tempDiv.innerHTML = content.body.replace(
                /<style[^>]*>[\s\S]*?<\/style>|<[^>]*>/g,
                '',
              )
              return tempDiv.textContent || tempDiv.innerText || ''
            })().length > 200 && '...'}
          </ContentExcerpt>
        </ContentBody>

        <ContentFooter>
          <ContentTags>
            {content.contentTags.map((tag) => (
              <Tag key={tag.name}>
                <span>＃</span>
                <span>{tag.name}</span>
              </Tag>
            ))}
          </ContentTags>
          <ContentStats>
            <LikeButton
              onClick={handleLikeClick}
              className={isLiked ? 'active' : ''}
            >
              <ContentLike />
            </LikeButton>
            <LikeCount>{likesCount}</LikeCount>
          </ContentStats>
        </ContentFooter>
      </Link>
    </CardWrapper>
  )
}

export const eclipseText = css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-line;
  line-height: 143%;
`

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding-bottom: 32px;
  border-bottom: 1px solid #e4e4e4;
  @media screen and (max-width: 768px) {
    padding-bottom: 32px;
  }
`

const ContentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 14px;
`

const ContentTitle = styled.h3`
  flex: 1;
  font-size: 20px;
  font-weight: 700;
  color: black;
  min-height: 2.8em;
  display: block;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: 'Noto Sans JP', sans-serif;

  @media (max-width: 768px) {
    min-height: initial;
  }
`

const BookmarkButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
`

const ContentAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 14px;
`

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
`

const AuthorAvatar = styled.div`
  width: 32px;
  height: 32px;
  background-color: #d1d5db;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const AuthorDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`

const CompanyName = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: #a8a9ac;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: 'Noto Sans JP', sans-serif;
`

const AuthorName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #6b7280;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: 'Noto Sans JP', sans-serif;
`

const ContentBody = styled.div`
  display: block;
  margin-bottom: 14px;

  &:after {
    content: '';
    display: block;
    clear: both;
  }
`

const ContentExcerpt = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  color: black;
  line-height: 160%;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: 'Noto Sans JP', sans-serif;
`

const ContentThumbnail = styled.div`
  float: right;
  width: 89px;
  position: relative;
  flex-shrink: 0;
  margin-left: 4px;
  overflow: hidden;

  &:after {
    content: '';
    display: block;
    padding-top: 100%;
  }
`

const ContentImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-width: 100%;
`

const ContentFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const ContentTags = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
`

const Tag = styled.span`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 8px 8px;
  background-color: #e4e4e4;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  color: #393e4c;
  font-family: 'Noto Sans JP', sans-serif;
  max-width: 100%;
  word-break: break-word;
`

const ContentStats = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
`

const LikeCount = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #5b5e63;
  line-height: 140%;
`

const LikeButton = styled.button`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    path {
      fill: #cdcdcd;
    }
  }

  &.active {
    svg {
      path {
        fill: #ff6c01;
      }
    }
  }
`

export const ContentBlur: VFC = () => (
  <ContentBlurWrapper>
    <BlurOverlay1 />
    <BlurOverlay2 />
    <BlurOverlay3 />
    <BlurOverlay4 />
  </ContentBlurWrapper>
)
const ContentBlurWrapper = styled.div`
  position: relative;
`
const OberlayCss = css`
  position: absolute;
  z-index: -1;
  border-radius: 50%;
  filter: blur(60px);
  @media screen and (max-width: 560px) {
    filter: blur(30px);
  }
`
const BlurOverlay1 = styled.div`
  @media screen and (max-width: 560px) {
    width: 130px;
    height: 100px;
    top: 50px;
    left: 50px;
  }
  ${OberlayCss}
  width: 345px;
  height: 221px;
  top: 80px;
  left: 50px;
  background: rgba(236, 104, 12, 1);
`
const BlurOverlay2 = styled.div`
  @media screen and (max-width: 560px) {
    display: none;
  }
  ${OberlayCss}
  width: 200px;
  height: 200px;
  top: -100px;
  right: -250px;
  background: rgba(236, 104, 12, 1);
`
const BlurOverlay3 = styled.div`
  @media screen and (max-width: 560px) {
    width: 250px;
    height: 180px;
    top: 70px;
    left: -100px;
  }
  ${OberlayCss}
  width: 461px;
  height: 340px;
  top: 253px;
  left: -200px;
  background: rgba(243, 152, 0, 0.5);
`
const BlurOverlay4 = styled.div`
  @media screen and (max-width: 560px) {
    width: 250px;
    height: 150px;
    top: 150px;
    right: -50px;
  }
  ${OberlayCss}
  width: 461px;
  height: 300px;
  top: 180px;
  right: -200px;
  background: rgba(243, 152, 0, 0.5);
`
